import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Scale, Upload, Download, Save, Loader2, Image as ImageIcon, Wand2 } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { fmtINR } from "@/lib/pricing";

export default function AITools() {
  const [tab, setTab] = useState("weight");
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
      <div className="mb-10">
        <div className="ornament mb-3"><Sparkles className="w-3 h-3 inline mr-1" /> AI Tools · Open to All</div>
        <h1 className="font-display text-4xl md:text-5xl text-ivory" style={{ fontWeight: 300 }}>Bhola AI Studio</h1>
        <p className="text-ivory/60 mt-4 text-sm max-w-2xl">Free for everyone. Estimate the gold, diamond and stone weight of any piece of jewellery from a photograph — or turn raw product shots into model photos, lifestyle creatives, and catalogue-ready cards. No prompt writing, no expertise needed.</p>
      </div>

      <div className="flex gap-2 border-b border-white/10">
        <TabBtn active={tab === "weight"} onClick={() => setTab("weight")} icon={<Scale className="w-4 h-4" />}>Weight Estimator</TabBtn>
        <TabBtn active={tab === "creative"} onClick={() => setTab("creative")} icon={<Wand2 className="w-4 h-4" />}>Creative Studio</TabBtn>
      </div>

      <div className="mt-8">
        {tab === "weight" && <WeightEstimator />}
        {tab === "creative" && <CreativeStudio />}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, children }) => (
  <button onClick={onClick} className={`px-5 py-3 text-sm tracking-wide border-b-2 -mb-px flex items-center gap-2 ${active ? "border-rose-gold text-rose-gold" : "border-transparent text-ivory/60 hover:text-ivory"}`}>
    {icon} {children}
  </button>
);

function WeightEstimator() {
  const ref = useRef();
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState({ type: "", size: "", karat: "" });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (hint.type) fd.append("jewellery_type_hint", hint.type);
      if (hint.size) fd.append("size_hint", hint.size);
      if (hint.karat) fd.append("karat_hint", hint.karat);
      const { data } = await api.post("/ai-tools/weight-estimate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Weight estimated");
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Could not process this image. Please try a clearer photo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="glass p-6 space-y-4">
        <div className="overline text-rose-gold">1 · Upload jewellery image</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <select value={hint.type} onChange={(e) => setHint({ ...hint, type: e.target.value })} className="bg-ink-900 border border-white/10 p-2 text-ivory" data-testid="hint-type">
            <option value="">Type (optional)</option>
            <option>Ring</option><option>Necklace</option><option>Earring</option><option>Bangle</option><option>Pendant</option>
          </select>
          <input value={hint.size} onChange={(e) => setHint({ ...hint, size: e.target.value })} placeholder="Size hint" className="bg-ink-900 border border-white/10 p-2 text-ivory" data-testid="hint-size" />
          <select value={hint.karat} onChange={(e) => setHint({ ...hint, karat: e.target.value })} className="bg-ink-900 border border-white/10 p-2 text-ivory" data-testid="hint-karat">
            <option value="">Karat (optional)</option>
            <option>14kt</option><option>18kt</option><option>22kt</option><option>24kt</option>
          </select>
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="weight-file-input" />
        <button onClick={() => ref.current?.click()} disabled={loading} className="w-full bg-rose-gold text-ink-900 py-4 tracking-widest uppercase text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="weight-upload-button">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Upload className="w-4 h-4" /> Choose Image</>}
        </button>
        {preview && <img src={preview} alt="" className="w-full max-h-64 object-contain border border-white/10 bg-ink-900" />}
        <p className="text-[11px] text-ivory/40">Estimates are AI-generated approximations. Verify before billing.</p>
      </div>

      <div className="glass p-6 space-y-4" data-testid="weight-result">
        <div className="overline text-rose-gold">2 · Estimate</div>
        {!result ? (
          <div className="text-ivory/40 text-sm py-16 text-center">Upload an image to see the estimate</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Type" value={result.estimate?.jewellery_type || "—"} />
              <Stat label="Karat" value={`${result.estimate?.estimated_karat || "—"}kt`} />
              <Stat label="Gold" value={`${n(result.estimate?.gold_weight_g)} g ± ${n(result.estimate?.gold_weight_margin, 2)}`} />
              <Stat label="Diamond" value={`${n(result.estimate?.diamond_weight_ct, 3)} ct ± ${n(result.estimate?.diamond_weight_margin, 3)}`} />
              <Stat label="Total" value={`${n(result.estimate?.total_weight_g)} g`} />
              <Stat label="Confidence" value={`${n(result.estimate?.confidence_percent, 0)}%`} />
            </div>
            {result.estimate?.notes && <div className="text-xs text-ivory/60 border-t border-white/10 pt-3">{result.estimate.notes}</div>}
            {result.price && (
              <div className="border-t border-rose-gold/30 pt-4 space-y-1.5">
                <div className="overline text-rose-gold">At today's rates</div>
                <Row label={`Gold value (${result.price.karat})`} value={fmtINR(result.price.gold_value)} />
                <Row label={`Making ${result.price.making_pct}%`} value={fmtINR(result.price.making_charges)} />
                {result.price.diamond_value > 0 && <Row label="Diamond" value={fmtINR(result.price.diamond_value)} />}
                <Row label="Subtotal" value={fmtINR(result.price.subtotal)} />
                <Row label="GST 3%" value={fmtINR(result.price.gst_3pct)} />
                <div className="flex justify-between pt-2 border-t border-rose-gold/30 font-display text-xl text-champagne">
                  <span className="overline text-rose-gold">Total</span>
                  <span>{fmtINR(result.price.total_incl_gst)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CreativeStudio() {
  const [mode, setMode] = useState("model");
  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        <ModeBtn active={mode === "model"} onClick={() => setMode("model")}>Model Photo</ModeBtn>
        <ModeBtn active={mode === "lifestyle"} onClick={() => setMode("lifestyle")}>Lifestyle Creative</ModeBtn>
        <ModeBtn active={mode === "catalogue_card"} onClick={() => setMode("catalogue_card")}>Catalogue Card</ModeBtn>
      </div>
      {mode === "model" && <ModelPhotoTool />}
      {mode === "lifestyle" && <LifestyleTool />}
      {mode === "catalogue_card" && <CatalogueCardTool />}
    </div>
  );
}

const ModeBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-4 py-2 text-xs tracking-widest uppercase border ${active ? "border-rose-gold bg-rose-gold/10 text-rose-gold" : "border-white/10 text-ivory/60"}`}>{children}</button>
);

function ModelPhotoTool() {
  return <ImageGenTool
    endpoint="/ai-tools/generate/model"
    fields={[
      { name: "jewellery_type", label: "Type", options: ["ring", "necklace", "earring", "bangle", "pendant"] },
      { name: "model_style", label: "Style", options: ["indian bridal", "modern editorial", "minimal casual"] },
      { name: "skin_tone", label: "Skin Tone", options: ["fair", "wheatish", "brown", "deep"] },
    ]}
    defaults={{ jewellery_type: "ring", model_style: "modern editorial", skin_tone: "wheatish" }}
    testid="tool-model"
  />;
}
function LifestyleTool() {
  return <ImageGenTool
    endpoint="/ai-tools/generate/lifestyle"
    fields={[
      { name: "occasion", label: "Occasion", options: ["bridal", "festive", "everyday", "gift"] },
      { name: "background", label: "Background", options: ["marble", "velvet", "floral", "gold foil", "dark luxury"] },
    ]}
    defaults={{ occasion: "bridal", background: "marble" }}
    testid="tool-lifestyle"
  />;
}
function CatalogueCardTool() {
  return <ImageGenTool endpoint="/ai-tools/generate/catalogue-card" fields={[]} defaults={{}} testid="tool-catalogue" />;
}

function ImageGenTool({ endpoint, fields, defaults, testid }) {
  const ref = useRef();
  const [params, setParams] = useState(defaults);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const generate = async () => {
    if (!file) { toast.error("Pick an image first"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(params).forEach(([k, v]) => fd.append(k, v));
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Generated!");
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Could not generate. Please try a clearer photo.");
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!result?.image_path) return;
    const a = document.createElement("a");
    a.href = fileUrl(result.image_path);
    a.download = "bhola-jewellers-creative.png";
    a.target = "_blank";
    a.click();
  };

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid={testid}>
      <div className="glass p-6 space-y-4">
        <div className="overline text-rose-gold">1 · Input</div>
        {fields.map((f) => (
          <div key={f.name}>
            <label className="overline text-ivory/50">{f.label}</label>
            <select value={params[f.name] || ""} onChange={(e) => setParams({ ...params, [f.name]: e.target.value })} className="mt-1 w-full bg-ink-900 border border-white/10 p-3 text-ivory capitalize" data-testid={`${testid}-${f.name}`}>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid={`${testid}-file`} />
        <button onClick={() => ref.current?.click()} className="w-full border border-rose-gold/40 py-3 text-sm text-ivory hover:bg-rose-gold/10 flex items-center justify-center gap-2">
          <Upload className="w-4 h-4" /> {file ? file.name : "Choose Image"}
        </button>
        {preview && <img src={preview} alt="" className="w-full max-h-64 object-contain border border-white/10 bg-ink-900" />}
        <button onClick={generate} disabled={loading || !file} className="w-full bg-rose-gold text-ink-900 py-4 tracking-widest uppercase text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`${testid}-generate`}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating your creative… (~15s)</> : <><Wand2 className="w-4 h-4" /> Generate</>}
        </button>
      </div>

      <div className="glass p-6 space-y-4">
        <div className="overline text-rose-gold">2 · Output</div>
        {!result ? (
          <div className="text-ivory/40 text-sm py-20 text-center">
            <ImageIcon className="w-10 h-10 mx-auto mb-3 text-rose-gold/25" />
            Your creative will appear here
          </div>
        ) : (
          <>
            <img src={fileUrl(result.image_path)} alt="" className="w-full object-contain border border-rose-gold/30 bg-ink-900" />
            <button onClick={download} className="w-full border border-rose-gold/40 py-3 text-sm text-ivory hover:bg-rose-gold/10 flex items-center justify-center gap-2" data-testid={`${testid}-download`}>
              <Download className="w-4 h-4" /> Download
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-3 bg-ink-900/50 border border-white/5">
    <div className="overline text-ivory/40">{label}</div>
    <div className="text-ivory mt-1">{value}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between text-ivory/70"><span>{label}</span><span>{value}</span></div>
);
const n = (v, d = 2) => Number(v || 0).toFixed(d);
